import { OnboardingBackdrop } from '@/components/onboarding/onboarding-backdrop';

/**
 * The ground under the journey and the reward screens.
 *
 * These screens used to be flat brand navy. At 390 px that reads as "an app
 * with a blue background"; at 1440 px it reads as a narrow column adrift on a
 * blue field, which is exactly what the owner called bland. So the journey
 * stands on a photograph too, and the app has one world from the welcome
 * screen to the certificate.
 *
 * It is the *second* photograph, though. Callers pass
 * `siteSettings.secondaryBackgroundUrl` — the ocean shot — and onboarding keeps
 * `home_background`, the lifesavers. That is what the two `site_settings`
 * fields are for, and it is the difference between arriving somewhere and
 * still being on the welcome screen. Both grounds carry an inlined thumbnail in
 * `onboarding-backdrop.tsx`, so the reveal is the same on either.
 *
 * Three layers, all of them paint-once:
 *
 * 1. `OnboardingBackdrop` — deliberately the *shared* layer rather than a copy,
 *    because it carries the inlined ~470-byte blurred thumbnail (LQIP) and the
 *    `decode()`-then-reveal sequence that stops the photograph popping in. A
 *    second implementation would drift from it within a ticket. The journey's
 *    own scrim is supplied by overriding `--bp-scrim` in `.journey-ground`, so
 *    nothing in the onboarding component has to know about this screen.
 * 2. the sun — one soft warm radial highlight over the water, which is what
 *    gives a large gradient somewhere to come *from*.
 * 3. grain — 3.5 % fractal noise, which is what stops a 1440 px gradient
 *    banding on a good display.
 *
 * Fixed to the viewport, `aria-hidden`, and entirely outside the layout: a
 * four-module trail is taller than a phone screen, and this way the content
 * scrolls over the photograph instead of stretching it. Nothing here can shift
 * anything, so it costs no CLS.
 */
export function JourneyGround({ src }: { src: string }) {
  return (
    <div aria-hidden className="journey-ground">
      <OnboardingBackdrop src={src} />
      <div className="journey-sun absolute inset-0" />
      <div className="journey-grain absolute inset-0" />
    </div>
  );
}
